<template>
  <div class="weather-container md:ml-6">
    <div class="weather-pill shadow lg:shadow-none" :class="{ 'weather-pill--loaded': weather.main }">
      <!-- Weather icon -->
      <div v-if="weather.weather" class="weather-icon">
        <img
          :src="`https://openweathermap.org/img/wn/${weather.weather[0].icon}@2x.png`"
          :alt="weather.weather[0].description"
          width="48"
          height="48"
        >
      </div>
      <div v-else class="weather-icon-skeleton" />

      <div class="weather-details">
        <!-- WEATHER SUMMARY -->
        <span v-if="weather.main" class="weather-condition">
          {{ toTitleCase(weather) }}
        </span>
        <!-- LOADING ANIMATION -->
        <div v-else class="w-24 h-3.5 bg-white/20 rounded animate-pulse" />

        <!-- TEMPERATURES -->
        <div v-if="weather.main" class="weather-temps">
          <span class="weather-temp-current">
            {{ convertFloat(weather.main.temp) }}°
          </span>
          <span class="weather-temp-range">
            {{ convertFloat(weather.main.temp_min) }}° / {{ convertFloat(weather.main.temp_max) }}°
          </span>
        </div>
        <div v-else class="w-16 h-3 bg-white/10 rounded animate-pulse mt-1" />
      </div>
    </div>

    <!-- FORECAST STRIP -->
    <div v-if="forecast.length" class="forecast-strip w-full lg:ml-auto xl:w-auto max-w-full">
      <div
        v-for="(period, idx) in forecast"
        :key="idx"
        class="forecast-day w-[14%] md:w-20"
        :class="{ 'forecast-day--night': !period.is_daytime }"
      >
        <span class="forecast-name truncate">{{ shortenName(period.name) }}</span>
        <img
          :src="`https://openweathermap.org/img/wn/${period.icon}@2x.png`"
          :alt="period.description"
          class="forecast-icon"
        >
        <span class="forecast-temp">{{ convertFloat(period.temp) }}°</span>
        <span class="forecast-desc">{{ period.description }}</span>
      </div>
    </div>
    <!-- Forecast loading skeleton -->
    <div v-else-if="forecastLoading" class="forecast-strip forecast-strip--loading">
      <div v-for="i in 7" :key="i" class="forecast-day-skeleton w-[14%] md:w-20">
        <div class="skel-name" />
        <div class="skel-icon" />
        <div class="skel-temp" />
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex"
import _ from 'lodash'
import { fromUnixTime, format } from 'date-fns'
import { getWeather, getForecast } from "@/services/weather.service"

export default {
  name: "WeatherComponent",
  data () {
    return {
      weather: {},
      forecast: [],
      forecastLoading: true,
    }
  },
  computed: {
    ...mapGetters([
      "getActiveSite"
    ]),
  },
  mounted() {
    this.loadWeather()
    this.loadForecast()
  },
  methods: {
    toTitleCase(data) {
      if (!data.weather) return
      const desc = data.weather[0].description
      return desc.replace(/\b\w/g, l => l.toUpperCase())
    },
    convertTime(data) {
      return format(fromUnixTime(data), "eeee | LLLL do | hh:mm bbb ")
    },
    convertFloat(data){
      return _.floor(data)
    },
    shortenName(name) {
      // "This Afternoon" → "This Aft", "Monday Night" → "Mon Night"
      const map = {
        'Sunday': 'Sun', 'Monday': 'Mon', 'Tuesday': 'Tue',
        'Wednesday': 'Wed', 'Thursday': 'Thu', 'Friday': 'Fri', 'Saturday': 'Sat',
        'This Afternoon': 'Today', 'This Evening': 'Tonight',
        'Overnight': 'Night'
      }
      for (const [full, short] of Object.entries(map)) {
        if (name.includes(full)) return name.replace(full, short)
      }
      return name
    },
    async loadWeather() {
      const site = this.getActiveSite
      const response = await getWeather(site.id, site.latitude, site.longitude)
      this.weather = response?.data || response
    },
    async loadForecast() {
      try {
        const site = this.getActiveSite
        const response = await getForecast(site.id, site.latitude, site.longitude)
        const data = response?.data || response
        // Take only daytime periods for a cleaner strip (up to 7 days)
        if (data && data.periods) {
          this.forecast = data.periods.filter(p => p.is_daytime).slice(0, 7)
        }
      } catch (e) {
        console.warn('Forecast load failed:', e)
      } finally {
        this.forecastLoading = false
      }
    }
  },
}
</script>

<style scoped>
.weather-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex: 1;
  min-width: 0;
  width: 100%;
}

.weather-pill {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 9999px;
  padding: 0.375rem 1.25rem 0.375rem 0.375rem;
  min-width: 180px;
  transition: background 0.3s ease, border-color 0.3s ease;
}
.weather-pill--loaded {
  background: rgba(34, 34, 34, 0.064);
}

.weather-icon {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.weather-icon img {
  width: 42px;
  height: 42px;
  filter: brightness(1.15);
}
.weather-icon-skeleton {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(26, 26, 26, 0.782);
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.weather-details {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.weather-condition {
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.9);
  line-height: 1.2;
  white-space: nowrap;
}

.weather-temps {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.weather-temp-current {
  font-size: 1.125rem;
  font-weight: 700;
  color: #000000;
  line-height: 1;
}

.weather-temp-range {
  font-size: 0.6875rem;
  font-weight: 500;
  color: rgba(45, 45, 45, 0.61);
  line-height: 1;
}

/* ── Forecast strip ─────────────────────────────── */
.forecast-strip {
  display: flex;
  gap: 0.125rem;
  overflow-x: auto;
  padding: 0.75rem 0 0.25rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.1) transparent;
  animation: forecast-fade-in 0.5s ease both;
  animation-delay: 0.3s;
  /* width: 100%; */
  /* max-width: 200px; */
  /* justify-content: flex-end; */
  /* overflow-x: scroll; */
}
.forecast-strip::-webkit-scrollbar {
  height: 3px;
}
.forecast-strip::-webkit-scrollbar-thumb {
  background: rgba(0,0,0,0.1);
  border-radius: 4px;
}

.forecast-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.125rem;
  /* min-width: 72px; */
  /* flex: 0 0 72px; */
  /* width: 72px; */
  flex-shrink: 0;
  padding: 0.5rem 0.375rem;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.04);
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
  cursor: default;
}
.forecast-day:hover {
  background: rgba(0, 0, 0, 0.06);
  /* transform: translateY(-2px); */
  /* box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06); */
}
.forecast-day--night {
  opacity: 0.65;
}

.forecast-name {
  font-size: 0.625rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  width: 100%;
}

.forecast-icon {
  width: 32px;
  height: 32px;
  filter: brightness(1.1);
}

.forecast-temp {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.forecast-desc {
  font-size: 0.5625rem;
  font-weight: 500;
  color: #94a3b8;
  text-align: center;
  line-height: 1.2;
  width: 100%;
  /* max-width: 70px; */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Loading skeleton */
.forecast-strip--loading {
  animation: none;
}
.forecast-day-skeleton {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  /* min-width: 72px; */
  padding: 0.5rem 0.375rem;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.02);
}
.skel-name {
  width: 32px;
  height: 8px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
  animation: pulse 2s ease infinite;
}
.skel-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.05);
  animation: pulse 2s ease infinite;
  animation-delay: 0.1s;
}
.skel-temp {
  width: 24px;
  height: 10px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.06);
  animation: pulse 2s ease infinite;
  animation-delay: 0.2s;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

@keyframes forecast-fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 768px) {
  .weather-pill {
    padding: 0.25rem 1rem 0.25rem 0.25rem;
    margin: 0 auto;
  }
  .forecast-strip {
    padding: 0.5rem 0 0.25rem;
  }
  .forecast-day {
    min-width: 64px;
  }
}

/* ── Dark mode ─────────────────────────────────── */
:root[data-theme="dark"] .weather-pill,
:root[data-theme="dark"] .weather-pill--loaded {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.10);
}
:root[data-theme="dark"] .weather-condition {
  color: rgba(255, 255, 255, 0.85);
}
:root[data-theme="dark"] .weather-temp-current {
  color: rgba(255, 255, 255, 0.95);
}
:root[data-theme="dark"] .weather-temp-range {
  color: rgba(255, 255, 255, 0.45);
}
:root[data-theme="dark"] .weather-icon img {
  filter: brightness(1.4);
}
:root[data-theme="dark"] .weather-icon-skeleton {
  background: rgba(255, 255, 255, 0.08);
}

/* Forecast dark mode */
:root[data-theme="dark"] .forecast-day {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.06);
}
:root[data-theme="dark"] .forecast-day:hover {
  background: rgba(255, 255, 255, 0.08);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.20);
}
:root[data-theme="dark"] .forecast-name {
  color: rgba(255, 255, 255, 0.50);
}
:root[data-theme="dark"] .forecast-temp {
  color: rgba(255, 255, 255, 0.90);
}
:root[data-theme="dark"] .forecast-desc {
  color: rgba(255, 255, 255, 0.35);
}
:root[data-theme="dark"] .forecast-icon {
  filter: brightness(1.4);
}
:root[data-theme="dark"] .forecast-strip::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
}
:root[data-theme="dark"] .forecast-day-skeleton {
  background: rgba(255, 255, 255, 0.03);
}
:root[data-theme="dark"] .skel-name,
:root[data-theme="dark"] .skel-icon,
:root[data-theme="dark"] .skel-temp {
  background: rgba(255, 255, 255, 0.06);
}
</style>